/**
 * Registration, update control, and explicit offline storage for one book at
 * a time. The service worker (`sw.ts`) never decides on its own to keep a
 * book: everything under `readcommons-offline-books` was put there by
 * `downloadBook()` below, at the reader's request, and nowhere else.
 *
 * Every export is a guarded no-op where the platform lacks the feature
 * (`serviceWorker`, `caches`, `navigator.storage`), so a browser without
 * offline support simply reads online, exactly as it does today.
 */

import { api } from "./api";
import type { BookDetail } from "./types";

/**
 * One file to keep, and whether the book is unusable without it.
 *
 * The distinction is not cosmetic. A book's record and its chapters are the
 * book; a page map, an alignment, a PDF or a narration are things the book
 * may or may not have, and the server answers 404 for the ones it does not.
 * Downloading with `cache.addAll` semantics, where any 404 fails the lot,
 * turns one absent extra into "this book cannot be kept offline at all".
 * That is exactly what happened to the first book tested: its index still
 * advertised an alignment the server had since started refusing, and the
 * whole download died on it.
 */
export interface OfflineAsset {
  url: string;
  required: boolean;
}

export interface OfflineBook {
  bookId: string;
  urls: string[];
  bytes: number;
  savedAt: string;
}

export interface DownloadProgress {
  bookId: string;
  done: number;
  total: number;
  bytes: number;
}

export interface StorageUsage {
  usageBytes: number;
  quotaBytes: number;
}

// Must match `OFFLINE_CACHE` in sw.ts exactly. The two files cannot share a
// constant: one is bundled into the page, the other into a worker with its
// own build entry, so the name is duplicated on purpose rather than imported.
const OFFLINE_CACHE = "readcommons-offline-books";

// A manifest entry is stored as a normal cache entry under a URL that is
// never actually fetched, so `listOffline()` and `removeBook()` can find a
// book's files without a second storage mechanism (IndexedDB) for one array
// of strings.
const MANIFEST_PREFIX = "https://offline.readcommons.invalid/manifest/";

function manifestKey(bookId: string): string {
  return `${MANIFEST_PREFIX}${encodeURIComponent(bookId)}`;
}

export function supportsOffline(): boolean {
  return "serviceWorker" in navigator && "caches" in window;
}

let registration: ServiceWorkerRegistration | null = null;
const updateListeners = new Set<() => void>();

function notifyUpdate(): void {
  for (const listener of updateListeners) listener();
}

/**
 * Registers `sw.js` under the app's own scope. Call once, after the app has
 * mounted; a failure here (unsupported browser, storage blocked by a private
 * window) is swallowed, never thrown, because the reader must work identically
 * with or without it.
 */
export async function register(): Promise<ServiceWorkerRegistration | null> {
  if (!supportsOffline()) return null;
  try {
    const base = import.meta.env.BASE_URL;
    registration = await navigator.serviceWorker.register(`${base}sw.js`, {
      scope: base,
      type: "module",
    });
    registration.addEventListener("updatefound", () => {
      const installing = registration?.installing;
      if (!installing) return;
      installing.addEventListener("statechange", () => {
        // A worker installed while another already controls the page is an
        // update waiting for `applyUpdate()`, not a fresh first install.
        if (installing.state === "installed" && navigator.serviceWorker.controller) {
          notifyUpdate();
        }
      });
    });
    return registration;
  } catch {
    return null;
  }
}

/** Subscribes to "a new version is installed and waiting". Returns the
 * unsubscribe function, in the usual React-effect shape. */
export function onUpdateAvailable(listener: () => void): () => void {
  updateListeners.add(listener);
  return () => updateListeners.delete(listener);
}

/**
 * Activates the waiting worker and reloads the page. The worker itself never
 * calls `skipWaiting` on install (see sw.ts): this is the only path that can
 * hand the interface over mid-session, and it exists so the caller can wait
 * for a safe moment, such as between chapters, rather than cutting off
 * playback under a listener.
 */
export async function applyUpdate(): Promise<void> {
  const waiting = registration?.waiting;
  if (!waiting) return;
  await new Promise<void>((resolve) => {
    navigator.serviceWorker.addEventListener("controllerchange", () => resolve(), {
      once: true,
    });
    waiting.postMessage("skip-waiting");
  });
  window.location.reload();
}

/**
 * Downloads every URL of one book (its document, its audio, whichever chapter
 * texts the caller lists) into the offline cache, reporting progress by file
 * count and by the bytes it can account for. A response without
 * `content-length` still downloads, it simply does not add to the byte total.
 *
 * Fetches go through the page's own network stack, not the service worker's
 * fetch handler, so nothing here depends on the worker being installed: a
 * book can be downloaded even from a browser tab that has no worker yet, as
 * long as the offline Cache Storage API itself is available.
 */
export function bookAssets(book: BookDetail): OfflineAsset[] {
  // The record first: it is small, it is what the reader screen asks for
  // before anything else, and a book without it does not open at all.
  const assets: OfflineAsset[] = [
    { url: `/api/books/${encodeURIComponent(book.id)}`, required: true },
  ];
  for (let i = 0; i < book.chapter_marks.length; i++) {
    assets.push({ url: `/api/books/${encodeURIComponent(book.id)}/chapters/${i}`, required: true });
  }
  // Everything below is optional in the strong sense: the server itself
  // answers 404 for an alignment it no longer trusts or a page map it never
  // built, and the index can promise one it will not serve. A book keeps
  // fine without them, so their absence is skipped, not raised.
  if (book.has_pagemap) {
    assets.push({ url: `/api/books/${encodeURIComponent(book.id)}/pagemap`, required: false });
  }
  // And only asked for alongside a narration: an alignment is a list of
  // timings inside a recording, so a book with no recording has nothing to
  // light up and no reason to spend a request finding that out.
  if (book.has_sync && book.has_audio) {
    assets.push({ url: `/api/books/${encodeURIComponent(book.id)}/sync`, required: false });
  }
  if (book.has_doc) assets.push({ url: api.docUrl(book.id, book.doc_rev), required: false });
  if (book.has_audio) assets.push({ url: api.audioUrl(book.id, book.audio_rev), required: false });
  return assets;
}

export async function downloadBook(
  bookId: string,
  assets: OfflineAsset[],
  onProgress?: (progress: DownloadProgress) => void,
): Promise<void> {
  if (!supportsOffline()) throw new Error("offline storage is not available in this browser");
  const cache = await caches.open(OFFLINE_CACHE);
  let bytes = 0;
  // Only what was really stored goes in the manifest: `removeBook()` deletes
  // by this list, and `listOffline()` reports it, so an entry for a file that
  // was skipped would be a book claiming a size it does not occupy.
  const stored: string[] = [];
  for (let i = 0; i < assets.length; i++) {
    const { url, required } = assets[i];
    const response = await fetch(url, { credentials: "same-origin" });
    if (!response.ok) {
      // A missing extra is a smaller book, not a failure. A missing chapter is
      // a book that opens on nothing, and that must be said out loud.
      if (!required) {
        onProgress?.({ bookId, done: i + 1, total: assets.length, bytes });
        continue;
      }
      // A half-kept book is worse than none: it takes room on the device, it
      // never appears in the shelf because no manifest was written, and so no
      // button can ever give that room back. Undo before raising.
      await Promise.all(stored.map((kept) => cache.delete(kept)));
      throw new Error(`could not download ${url}: HTTP ${response.status}`);
    }
    const length = Number(response.headers.get("content-length"));
    if (Number.isFinite(length)) bytes += length;
    await cache.put(url, response);
    stored.push(url);
    onProgress?.({ bookId, done: i + 1, total: assets.length, bytes });
  }
  const manifest: OfflineBook = { bookId, urls: stored, bytes, savedAt: new Date().toISOString() };
  await cache.put(manifestKey(bookId), new Response(JSON.stringify(manifest)));
}

/** Removes one book's cached files and its manifest entry. A no-op, not an
 * error, if the book was never downloaded. */
export async function removeBook(bookId: string): Promise<void> {
  if (!supportsOffline()) return;
  const cache = await caches.open(OFFLINE_CACHE);
  const key = manifestKey(bookId);
  const manifestResponse = await cache.match(key);
  if (manifestResponse) {
    const manifest = (await manifestResponse.json()) as OfflineBook;
    await Promise.all(manifest.urls.map((url) => cache.delete(url)));
  }
  await cache.delete(key);
}

/**
 * The downloaded books as the library screen wants them: whole records, not
 * just ids. Each one is fetched by its normal URL, which the service worker
 * answers from the offline cache without touching the network, so this works
 * with no server and returns nothing at all rather than throwing when a book
 * has been evicted under it.
 */
export async function offlineShelf(): Promise<BookDetail[]> {
  const saved = await listOffline();
  const books = await Promise.all(
    saved.map(async (entry) => {
      try {
        const response = await fetch(`/api/books/${encodeURIComponent(entry.bookId)}`);
        if (!response.ok) return null;
        return (await response.json()) as BookDetail;
      } catch {
        return null;
      }
    }),
  );
  return books.filter((book): book is BookDetail => book !== null);
}

/** Every book currently kept for offline reading, most recently saved first. */
export async function listOffline(): Promise<OfflineBook[]> {
  if (!supportsOffline()) return [];
  const cache = await caches.open(OFFLINE_CACHE);
  const keys = await cache.keys();
  const books: OfflineBook[] = [];
  for (const request of keys) {
    if (!request.url.startsWith(MANIFEST_PREFIX)) continue;
    const response = await cache.match(request);
    if (!response) continue;
    books.push((await response.json()) as OfflineBook);
  }
  return books.sort((a, b) => b.savedAt.localeCompare(a.savedAt));
}

/** Wraps `navigator.storage.estimate()`, missing on some browsers and on any
 * non-secure context: null rather than a throw, so a caller can hide the
 * usage figure instead of crashing a settings panel over it. */
export async function estimateUsage(): Promise<StorageUsage | null> {
  if (!("storage" in navigator) || !navigator.storage.estimate) return null;
  const estimate = await navigator.storage.estimate();
  return { usageBytes: estimate.usage ?? 0, quotaBytes: estimate.quota ?? 0 };
}
